import React from "react";
import { getUserFacingError } from "../../utils/userFacingError";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import { RegistrationLegalNotice } from "../../components/RegistrationLegalNotice";
import {
  clearAccessRequestSubmissionId,
  resolveAccessRequestSubmissionId,
  type AccessRequestIntake,
  submitAccessRequest,
} from "../../services/accessRequestOnboarding";
import { verificationAPI } from "../../services/verificationAPI";
import "./access-request.css";

type LookupResult = "ok" | "blocked" | "error" | "invalid";

const defaultForm: AccessRequestIntake = {
  companyName: "",
  cnpj: "",
  companyEmail: "",
  companyPhone: "",
  country: "Brasil",
  state: "",
  city: "",
  zipCode: "",
  street: "",
  numberStreet: "",
  neighborhood: "",
  applicantName: "",
  applicantEmail: "",
  applicantPhone: "",
};

const stepFields: Record<number, (keyof AccessRequestIntake)[]> = {
  0: ["cnpj", "companyEmail"],
  1: ["applicantName", "applicantEmail", "applicantPhone"],
};

function maskCNPJ(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

function maskCEP(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return digits.length > 5 ? digits.replace(/^(\d{5})(\d)/, "$1-$2") : digits;
}

function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d{1,4})$/, "$1-$2");
  }
  return digits.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d{1,4})$/, "$1-$2");
}

function Field({
  id,
  label,
  required,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="wizard-field">
      <label htmlFor={id}>
        {label}
        {required && <span className="required">*</span>}
      </label>
      {children}
      {error && (
        <span id={`${id}-error`} className="wizard-field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

function DiamondStepper({ step }: { step: number }) {
  const labels = ["Empresa", "Solicitante"];
  return (
    <ol className="wizard-stepper" aria-label={`Etapa ${step + 1} de ${labels.length}`}>
      {labels.map((label, index) => (
        <React.Fragment key={label}>
          {index > 0 && (
            <li className={`wizard-line ${index <= step ? "completed" : ""}`} aria-hidden="true" />
          )}
          <li className="wizard-diamond-wrapper" aria-current={index === step ? "step" : undefined}>
            <div
              className={`wizard-diamond ${
                index < step ? "completed" : index === step ? "active" : "future"
              }`}
            />
            <span className="wizard-diamond-label">{label}</span>
          </li>
        </React.Fragment>
      ))}
    </ol>
  );
}

function fieldError(field: keyof AccessRequestIntake, form: AccessRequestIntake): string {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  switch (field) {
    case "cnpj":
      return form.cnpj.replace(/\D/g, "").length === 14 ? "" : "CNPJ deve ter 14 dígitos.";
    case "companyEmail":
      return emailPattern.test(form.companyEmail.trim()) ? "" : "E-mail corporativo inválido.";
    case "applicantName":
      return form.applicantName.trim().length >= 3 ? "" : "Informe seu nome completo.";
    case "applicantEmail":
      return emailPattern.test(form.applicantEmail.trim()) ? "" : "E-mail do solicitante inválido.";
    case "applicantPhone":
      return form.applicantPhone.replace(/\D/g, "").length >= 10
        ? ""
        : "Telefone deve incluir DDD e número.";
    default:
      return "";
  }
}

function isRegistryError(message?: string): boolean {
  return Boolean(
    message &&
    (message.includes("Receita") ||
      message.includes("Situação") ||
      message.includes("ATIVAS") ||
      message.includes("cadastrada")),
  );
}

export const AccessRequestPage: React.FC = () => {
  const [submissionId] = React.useState(() =>
    resolveAccessRequestSubmissionId(window.sessionStorage),
  );
  const [step, setStep] = React.useState(0);
  const [stepDirection, setStepDirection] = React.useState<"forward" | "backward">("forward");
  const [form, setForm] = React.useState<AccessRequestIntake>(defaultForm);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [cnpjLoading, setCnpjLoading] = React.useState(false);
  const lookupSequence = React.useRef(0);

  const setField = React.useCallback((field: keyof AccessRequestIntake, value: string) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setSubmitError(null);
    setErrors((previous) => {
      if (!previous[field]) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const setCnpj = React.useCallback((value: string) => {
    const masked = maskCNPJ(value);
    lookupSequence.current += 1;
    setCnpjLoading(false);
    setSubmitError(null);
    setForm((previous) => ({
      ...previous,
      cnpj: masked,
      companyName: "",
      companyEmail: "",
      companyPhone: "",
      state: "",
      city: "",
      zipCode: "",
      street: "",
      numberStreet: "",
      neighborhood: "",
    }));
    setErrors((previous) => {
      if (!previous.cnpj) return previous;
      const next = { ...previous };
      delete next.cnpj;
      return next;
    });
  }, []);

  function validateField(field: keyof AccessRequestIntake) {
    const message = fieldError(field, form);
    setErrors((previous) => {
      if (isRegistryError(previous[field])) return previous;
      const next = { ...previous };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  }

  function validateStep(currentStep: number): boolean {
    const nextErrors: Record<string, string> = {};
    for (const field of stepFields[currentStep]) {
      const message = fieldError(field, form);
      if (message) nextErrors[field] = message;
    }
    setErrors((previous) => {
      const merged = { ...nextErrors };
      for (const field of stepFields[currentStep]) {
        if (!merged[field] && isRegistryError(previous[field])) merged[field] = previous[field];
      }
      return merged;
    });
    return (
      Object.keys(nextErrors).length === 0 &&
      !stepFields[currentStep].some((field) => isRegistryError(errors[field]))
    );
  }

  async function companyAlreadyExists(cnpj: string): Promise<boolean> {
    try {
      const response = await verificationAPI.get("/access-request/check", {
        params: { cnpj: cnpj.replace(/\D/g, "") },
      });
      if (response.data?.exists) {
        setErrors((previous) => ({
          ...previous,
          cnpj: "Já existe uma empresa cadastrada com este CNPJ.",
        }));
        return true;
      }
    } catch {
      // The server repeats this check on submit. A transient preflight failure
      // must not prevent a legitimate applicant from continuing.
    }
    return false;
  }

  async function lookupCnpj(rawCnpj: string): Promise<LookupResult> {
    const cnpj = rawCnpj.replace(/\D/g, "");
    if (cnpj.length !== 14) return "invalid";
    const sequence = ++lookupSequence.current;
    setCnpjLoading(true);
    try {
      if (await companyAlreadyExists(cnpj)) return "blocked";
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      if (sequence !== lookupSequence.current) return "blocked";
      if (response.status === 404) {
        setErrors((previous) => ({
          ...previous,
          cnpj: "CNPJ não encontrado na Receita Federal.",
        }));
        return "blocked";
      }
      if (!response.ok) return "error";
      const data = await response.json();
      const active = data.situacao_cadastral === 2 || data.descricao_situacao_cadastral === "ATIVA";
      if (!active) {
        setErrors((previous) => ({
          ...previous,
          cnpj: "Este CNPJ não está ativo na Receita Federal. Apenas empresas ativas podem solicitar acesso.",
        }));
        return "blocked";
      }

      const registryPhone = String(data.ddd_telefone_1 || "").replace(/\D/g, "");
      const registryEmail = String(data.email || "").trim();
      setForm((previous) => ({
        ...previous,
        companyName:
          String(data.razao_social || data.nome_fantasia || "").trim() || previous.companyName,
        companyEmail: previous.companyEmail || registryEmail,
        companyPhone:
          previous.companyPhone || (registryPhone.length >= 10 ? maskPhone(registryPhone) : ""),
        country: "Brasil",
        state: String(data.uf || previous.state),
        city: String(data.municipio || previous.city),
        zipCode: maskCEP(String(data.cep || previous.zipCode)),
        street: String(data.logradouro || previous.street),
        numberStreet:
          data.numero && String(data.numero).toUpperCase() !== "SN"
            ? String(data.numero)
            : previous.numberStreet,
        neighborhood: String(data.bairro || previous.neighborhood),
      }));
      setErrors((previous) => {
        const next = { ...previous };
        delete next.cnpj;
        return next;
      });
      return "ok";
    } catch {
      return "error";
    } finally {
      if (sequence === lookupSequence.current) setCnpjLoading(false);
    }
  }

  async function handleNext() {
    if (!validateStep(0)) return;
    const lookup = await lookupCnpj(form.cnpj);
    if (lookup === "blocked" || lookup === "invalid") return;
    if (lookup === "error") {
      setErrors((previous) => ({
        ...previous,
        cnpj: "Não foi possível consultar a Receita Federal. Tente novamente.",
      }));
      return;
    }
    setStepDirection("forward");
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleBack() {
    setStepDirection("backward");
    setSubmitError(null);
    setStep(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit() {
    if (!validateStep(1)) return;
    if (!form.companyName.trim()) {
      setStepDirection("backward");
      setStep(0);
      setErrors((previous) => ({
        ...previous,
        cnpj: "Confirme novamente o CNPJ antes de enviar.",
      }));
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitAccessRequest(form, submissionId);
      clearAccessRequestSubmissionId(window.sessionStorage);
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error: any) {
      setSubmitError(
        getUserFacingError(error, {
          context: "accessRequest",
          fallback: "Não foi possível enviar sua solicitação agora. Tente novamente em instantes.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const address = [
    [form.street, form.numberStreet].filter(Boolean).join(", "),
    form.neighborhood,
    [form.city, form.state].filter(Boolean).join("/"),
    form.zipCode,
  ]
    .filter((part) => part.trim())
    .join(" · ");

  if (step === 2) {
    return (
      <div className="access-request-page">
        <div className="wizard-content">
          <img
            className="wizard-logo"
            src={`${window.location.origin}/assets/Logo_DAKASA_V1_Preto.png`}
            alt="DaKasa LTDA"
          />
          <div className="wizard-success">
            <span className="wizard-success-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faCircleCheck} />
            </span>
            <h1 className="wizard-success-title">Solicitação recebida</h1>
            <p className="wizard-success-text">
              Recebemos o cadastro inicial. Se ele puder seguir, enviaremos ao e-mail do solicitante
              um link para confirmar identidade e vínculo com a empresa. Este envio ainda não
              significa aprovação nem criação de conta.
            </p>
            <a href="/" className="wizard-success-btn">
              Voltar para o início
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="access-request-page">
      <div className="wizard-content">
        <img
          className="wizard-logo"
          src={`${window.location.origin}/assets/Logo_DAKASA_V1_Preto.png`}
          alt="DaKasa LTDA"
        />
        <DiamondStepper step={step} />
        <form
          className="wizard-form"
          noValidate
          aria-busy={submitting || cnpjLoading}
          onSubmit={(event) => {
            event.preventDefault();
            if (step === 0) void handleNext();
            else void handleSubmit();
          }}
        >
          <div key={step} className={`wizard-step ${stepDirection}`}>
            {step === 0 ? (
              <>
                <h1 className="wizard-step-title">Dados da empresa</h1>
                <p className="wizard-step-subtitle">
                  Confirmamos os dados cadastrais básicos antes de receber a solicitação.
                </p>
                <div className="wizard-fields">
                  <Field id="cnpj" label="CNPJ" required error={errors.cnpj}>
                    <input
                      id="cnpj"
                      type="text"
                      inputMode="numeric"
                      value={form.cnpj}
                      onChange={(event) => setCnpj(event.target.value)}
                      onBlur={() => {
                        validateField("cnpj");
                        if (form.cnpj.replace(/\D/g, "").length === 14) {
                          void lookupCnpj(form.cnpj);
                        }
                      }}
                      placeholder="00.000.000/0001-00"
                      className={errors.cnpj ? "has-error" : ""}
                      aria-invalid={Boolean(errors.cnpj)}
                      aria-describedby={errors.cnpj ? "cnpj-error" : undefined}
                      autoComplete="off"
                    />
                    {cnpjLoading && (
                      <span className="wizard-field-hint">Consultando a Receita Federal…</span>
                    )}
                  </Field>

                  {form.companyName && !cnpjLoading && (
                    <div className="wizard-cnpj-confirm" role="status">
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        className="wizard-cnpj-confirm-icon"
                        aria-hidden="true"
                      />
                      <div className="wizard-cnpj-confirm-body">
                        <strong>{form.companyName}</strong>
                        {address && <span>{address}</span>}
                      </div>
                    </div>
                  )}

                  <Field
                    id="companyEmail"
                    label="E-mail corporativo"
                    required
                    error={errors.companyEmail}
                  >
                    <input
                      id="companyEmail"
                      type="email"
                      value={form.companyEmail}
                      onChange={(event) => setField("companyEmail", event.target.value)}
                      onBlur={() => validateField("companyEmail")}
                      placeholder="contato@empresa.com.br"
                      maxLength={254}
                      className={errors.companyEmail ? "has-error" : ""}
                      aria-invalid={Boolean(errors.companyEmail)}
                      aria-describedby={errors.companyEmail ? "companyEmail-error" : undefined}
                      autoComplete="section-company email"
                    />
                  </Field>

                  <Field id="companyPhone" label="Telefone da empresa">
                    <input
                      id="companyPhone"
                      type="tel"
                      inputMode="tel"
                      value={form.companyPhone}
                      onChange={(event) => setField("companyPhone", maskPhone(event.target.value))}
                      placeholder="(11) 3333-0000"
                      autoComplete="section-company tel"
                    />
                  </Field>
                </div>
              </>
            ) : (
              <>
                <h1 className="wizard-step-title">Quem está solicitando?</h1>
                <p className="wizard-step-subtitle">
                  Precisamos apenas de um contato agora. A verificação de identidade acontece
                  depois, se a solicitação avançar.
                </p>
                <div className="wizard-fields">
                  <Field
                    id="applicantName"
                    label="Nome completo"
                    required
                    error={errors.applicantName}
                  >
                    <input
                      id="applicantName"
                      type="text"
                      value={form.applicantName}
                      onChange={(event) => setField("applicantName", event.target.value)}
                      onBlur={() => validateField("applicantName")}
                      placeholder="Seu nome completo"
                      maxLength={150}
                      className={errors.applicantName ? "has-error" : ""}
                      aria-invalid={Boolean(errors.applicantName)}
                      aria-describedby={errors.applicantName ? "applicantName-error" : undefined}
                      autoComplete="section-applicant name"
                    />
                  </Field>

                  <Field
                    id="applicantEmail"
                    label="Seu e-mail"
                    required
                    error={errors.applicantEmail}
                  >
                    <input
                      id="applicantEmail"
                      type="email"
                      value={form.applicantEmail}
                      onChange={(event) => setField("applicantEmail", event.target.value)}
                      onBlur={() => validateField("applicantEmail")}
                      placeholder="voce@empresa.com.br"
                      maxLength={254}
                      className={errors.applicantEmail ? "has-error" : ""}
                      aria-invalid={Boolean(errors.applicantEmail)}
                      aria-describedby={errors.applicantEmail ? "applicantEmail-error" : undefined}
                      autoComplete="section-applicant email"
                    />
                  </Field>

                  <Field
                    id="applicantPhone"
                    label="Seu telefone"
                    required
                    error={errors.applicantPhone}
                  >
                    <input
                      id="applicantPhone"
                      type="tel"
                      inputMode="tel"
                      value={form.applicantPhone}
                      onChange={(event) =>
                        setField("applicantPhone", maskPhone(event.target.value))
                      }
                      onBlur={() => validateField("applicantPhone")}
                      placeholder="(11) 91234-5678"
                      className={errors.applicantPhone ? "has-error" : ""}
                      aria-invalid={Boolean(errors.applicantPhone)}
                      aria-describedby={errors.applicantPhone ? "applicantPhone-error" : undefined}
                      autoComplete="section-applicant tel"
                    />
                  </Field>

                  <div className="wizard-kyb-assurance">
                    <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
                    <div>
                      <strong>Nenhum documento é necessário agora</strong>
                      <span>
                        Se a análise cadastral inicial avançar, enviaremos um link protegido para
                        verificar sua identidade e seus poderes de representação.
                      </span>
                    </div>
                  </div>
                  <RegistrationLegalNotice />
                </div>
              </>
            )}
          </div>

          {submitError && (
            <div className="wizard-error-banner" role="alert">
              {submitError}
            </div>
          )}

          <div className="wizard-buttons">
            {step === 1 ? (
              <button type="button" className="wizard-btn-back" onClick={handleBack}>
                Voltar
              </button>
            ) : (
              <span />
            )}
            <button type="submit" className="wizard-btn-next" disabled={submitting || cnpjLoading}>
              {submitting
                ? "Enviando…"
                : cnpjLoading
                  ? "Consultando CNPJ…"
                  : step === 0
                    ? "Avançar para solicitante"
                    : "Enviar solicitação"}
            </button>
          </div>

          <p className="wizard-login-hint">
            Já possui acesso? <a href="/auth">Entrar</a>
          </p>
        </form>
      </div>
    </div>
  );
};
